import React from "react";
import SEO from "../../../components/SEO";
import { Link } from "react-router-dom";
import img21 from "../../../assets/img21.jpg";
import img22 from "../../../assets/img22.webp";
import img23 from "../../../assets/img23.webp";
import img24 from "../../../assets/img24.webp";
import img25 from "../../../assets/img25.webp";
import img26 from "../../../assets/img26.webp";

export default function ParticipatingPolicies() {
  return (
    <>
      <SEO
        title="Assurance vie permanente participante au Canada - einsured.ca"
        description="Découvrez l’assurance-vie participante au Canada, avec une couverture à vie et des dividendes potentiels pour les titulaires de police."
        keywords="Assurance vie permanente participante, police participante Canada, dividendes assurance vie, dividendes assurance vie entière"
      />

      {/* HERO SECTION */}
      <section
        className="relative min-h-[60vh] sm:min-h-[70vh] md:min-h-[80vh] bg-cover bg-center flex items-center justify-center md:justify-start px-4 sm:px-6 md:px-20"
        style={{ backgroundImage: `url(${img21})` }}
      >
        <div className="absolute inset-0 bg-black/40"></div>

        <div className="w-full max-w-[1380px] mx-auto">
          <div className="relative z-10 text-white max-w-3xl text-center md:text-left">
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 leading-tight">
              Polices participantes et avantages des dividendes
            </h1>
            <p className="text-base sm:text-lg md:text-xl font-semibold mb-6 sm:mb-8">
              Protégez vos proches et contribuez à votre croissance financière
            </p>
            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold text-base sm:text-lg px-8 py-3 rounded-full transition"
            >
              Commencer
            </a>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-24 bg-white text-gray-800 text-center">
        <div className="max-w-[1380px] mx-auto">
          <div className="max-w-5xl mx-auto">
            <img
              src={img22}
              alt="Assurance vie permanente"
              className="rounded-3xl shadow-md mb-8 sm:mb-10 w-full"
            />
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-4 sm:mb-6">
              Polices participantes et avantages des dividendes
            </h2>
            <p className="text-base sm:text-lg md:text-xl leading-relaxed text-gray-700">
              Parmi les différents types d'{" "}
              <Link
                to="/fr/assurance-vie-permanente"
                className="text-sky-600 hover:underline font-medium"
              >
                assurance vie permanente
              </Link>
              , la police participante se distingue par ses avantages uniques,
              notamment la possibilité de recevoir des dividendes. Chez{" "}
              <Link to="/" className="text-sky-600 hover:underline font-medium">
                einsured.ca
              </Link>
              , nous sommes là pour vous aider à comprendre ces polices et comment
              elles peuvent fonctionner à votre avantage.
            </p>
          </div>
        </div>
      </section>

      {/* PARTICIPATING POLICY & DIVIDEND DISTRIBUTION */}
      <section className="py-14 sm:py-16 md:py-24 px-4 sm:px-6 md:px-24 bg-white text-gray-800">
        <div className="max-w-[1380px] mx-auto">
          <div className="max-w-6xl mx-auto space-y-16 sm:space-y-20 md:space-y-24">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-4 sm:mb-6">
                  Qu'est-ce qu'une police d'assurance vie participante ?
                </h2>
                <p className="text-base sm:text-lg leading-relaxed text-gray-700">
                  Une police d'assurance vie participante, également connue sous le nom de
                  « police par », offre bien plus qu'une simple prestation de décès. Elle vous
                  permet de partager le succès financier de la compagnie d'assurance grâce au
                  versement de dividendes. Ces polices sont conçues non seulement pour protéger
                  vos proches, mais aussi pour contribuer à votre croissance financière au fil
                  du temps.
                </p>
              </div>
              <div>
                <img
                  loading="lazy"
                  src={img23}
                  alt="Police d'assurance vie permanente"
                  className="rounded-3xl shadow-md w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div className="order-2 md:order-1">
                <img
                  loading="lazy"
                  src={img24}
                  alt="Distribution des dividendes"
                  className="rounded-3xl shadow-md w-full"
                />
              </div>
              <div className="order-1 md:order-2">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-4 sm:mb-6">
                  Distribution des dividendes : une caractéristique attrayante
                </h2>
                <p className="text-base sm:text-lg leading-relaxed text-gray-700">
                  L'un des principaux avantages des polices participantes est la possibilité
                  de recevoir des dividendes. Il s'agit de paiements versés aux titulaires de
                  police à partir des bénéfices de l'assureur, généralement distribués
                  annuellement. Bien que les dividendes ne soient pas garantis, ils sont
                  couramment versés par des compagnies d'assurance financièrement solides.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DIVIDEND OPTIONS */}
      <section className="py-14 sm:py-16 md:py-24 px-4 sm:px-6 md:px-24 bg-sky-900 bg-opacity-90 text-gray-800">
        <div className="max-w-[1380px] mx-auto">
          <div className="max-w-6xl mx-auto text-center">
            <div className="inline-block bg-sky-400 text-white text-xl sm:text-2xl md:text-3xl font-semibold px-6 sm:px-10 py-3 rounded-full mb-10 sm:mb-16 shadow-md">
              Options de dividendes
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 md:gap-8">
              {[
                {
                  title: "Versement en espèces",
                  desc: "Recevez des dividendes directement en espèces, vous offrant des fonds immédiats.",
                },
                {
                  title: "Réduction de prime",
                  desc: "Utilisez les dividendes pour réduire vos primes d'assurance, diminuant ainsi vos dépenses.",
                },
                {
                  title: "Assurance libérée additionnelle",
                  desc: "Souscrivez une couverture supplémentaire sans payer de supplément, augmentant la prestation de décès de votre police.",
                },
                {
                  title: "Accumulation de dividendes",
                  desc: "Laissez les dividendes chez l'assureur pour gagner des intérêts et augmenter votre valeur de rachat au fil du temps.",
                },
                {
                  title: "Remboursement de prêt",
                  desc: "Appliquez les dividendes au remboursement des prêts sur police, réduisant ainsi vos passifs.",
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="bg-white rounded-3xl shadow-md p-6 sm:p-8 text-left"
                >
                  <h3 className="text-lg sm:text-xl font-bold text-[#49628b] mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed text-base sm:text-lg">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE / DIVIDEND VESTING */}
      <section className="py-14 sm:py-16 md:py-24 px-4 sm:px-6 md:px-24 bg-white text-gray-800">
        <div className="max-w-[1380px] mx-auto">
          <div className="max-w-6xl mx-auto space-y-16 sm:space-y-20 md:space-y-24">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-4 sm:mb-6">
                  Pourquoi choisir une police participante ?
                </h2>
                <p className="text-base sm:text-lg leading-relaxed text-gray-700">
                  Avec le potentiel de dividendes, l'avantage de l'acquisition des dividendes
                  et la capacité de faire croître la valeur de rachat, ces polices offrent un
                  moyen polyvalent et fiable d'atteindre des objectifs financiers à long terme.
                  Que vous soyez concentré sur la création de richesse, la sécurité familiale
                  ou la planification de la retraite, une police d'assurance vie participante
                  est un choix judicieux.
                </p>
              </div>
              <div>
                <img
                  loading="lazy"
                  src={img25}
                  alt="Police d'assurance vie"
                  className="rounded-3xl shadow-md w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div className="order-2 md:order-1">
                <img
                  loading="lazy"
                  src={img26}
                  alt="Famille profitant du temps en plein air"
                  className="rounded-3xl shadow-md w-full"
                />
              </div>
              <div className="order-1 md:order-2">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-4 sm:mb-6">
                  Acquisition des dividendes : sécuriser vos gains
                </h2>
                <p className="text-base sm:text-lg leading-relaxed text-gray-700">
                  Une caractéristique importante des polices participantes est l'acquisition
                  des dividendes. Cela signifie qu'une fois qu'un dividende est déclaré et
                  crédité à votre police, il ne peut pas être retiré, même si les dividendes
                  futurs sont inférieurs ou ne sont pas versés du tout. L'acquisition des
                  dividendes offre une couche de sécurité supplémentaire, garantissant que la
                  valeur accumulée dans votre police est préservée au fil du temps.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-14 sm:pb-16 text-center px-4">
        <a
          href="https://book.einsured.ca/widget/bookings/ei-team"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-sky-900 font-semibold text-base sm:text-lg px-8 sm:px-10 py-3 sm:py-4 rounded-full shadow-md transition"
        >
          Nous contacter
        </a>
      </section>
    </>
  );
}
